import type { ComponentRef, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  ViewChild,
  ViewContainerRef,
  output,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import {
  BehaviorSubject,
  Subject,
  switchMap,
  take,
  combineLatest,
  takeUntil,
  pairwise,
  startWith,
} from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import type { SelectOption } from '@core/user-interface-legacy';
import { Select2Component } from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import type { ViewFilterDef, ViewFilterInputDef, FilterControl } from '@core/views-types';

import { ViewsAnalytics } from '../../../analytics/views-analytics';
import type { InputComponentContext } from '../../input-types/types';
import { injectInputServices } from '../tokens';
import { FilterSelectComponent } from './filter-select/filter-select.component';
import type { InputTypeComponent } from './types';

@Component({
  selector: 'views-filter',
  imports: [ReactiveFormsModule, Select2Component, FilterSelectComponent],
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.wrap]': 'filterInputDefs.length > 2',
  },
})
export class FilterComponent implements OnChanges, OnInit, OnDestroy {
  private readonly cdRef = inject(ChangeDetectorRef);

  @Input() public dataSourceId: string;
  @Input() public filterUuid: string;
  @Input() public filterDef: ViewFilterDef;
  @Input() public filterInputDefs: ViewFilterInputDef[];
  @Input() public filterControl: FilterControl;
  @Input() public filterState: unknown[];

  public filterStateChanges = output<unknown[]>();
  public conditionOptions: SelectOption[];

  @ViewChild('inputsRef', { read: ViewContainerRef, static: true })
  private inputsVCRef: ViewContainerRef;

  private untilDestroyed = injectUntilDestroyed();
  private destroyInputs$ = new Subject<void>();
  private filterControl$ = new BehaviorSubject<FilterControl | null>(null);
  private inputComponentsRef: Array<ComponentRef<InputTypeComponent<any, unknown, any>>> = [];
  private inputServices = injectInputServices();
  private viewsAnalytics = inject(ViewsAnalytics);

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.filterDef && this.filterDef) {
      this.conditionOptions = this.filterDef.conditions.map(condition => ({
        label: condition.label,
        value: condition.id,
      }));
    }

    if (changes.filterInputDefs) {
      this.createInputComponents();
    }

    if (changes.filterControl && this.filterControl) {
      this.filterControl$.next(this.filterControl);
    }

    if (changes.filterControl || changes.filterState) {
      this.inputComponentsRef.forEach((_, index) => {
        this.updateOptionInputs(index);
      });
    }
  }

  public ngOnInit() {
    this.filterControl$
      .pipe(
        filterBoolean,
        switchMap(filterControl => filterControl.valueChanges),
        this.untilDestroyed,
      )
      .subscribe(() => this.cdRef.detectChanges());

    this.filterControl$
      .pipe(
        filterBoolean,
        switchMap(({ controls: { id } }) => id.valueChanges.pipe(startWith(id.value))),
        pairwise(),
        this.untilDestroyed,
      )
      .subscribe(([previousFilterId, nextFilterId]) => {
        this.viewsAnalytics.changeFilter({ previousFilterId, nextFilterId });
      });
  }

  public ngOnDestroy() {
    this.disposeInputComponents();
  }

  private createInputComponents() {
    this.disposeInputComponents();
    if (!this.filterInputDefs.length) return;

    const filterEvents: InputComponentContext['filterEvents'] = new Subject();
    this.inputComponentsRef = this.filterInputDefs.map(input => {
      const inputService = this.inputServices[input.type];
      const componentRef = inputService.createInputComponent(
        this.inputsVCRef,
        this.destroyInputs$,
        {
          dataSourceId: this.dataSourceId,
          filterDef: <ViewFilterDef>this.filterDef,
          inputDef: input,
          inputs: this.filterInputDefs,
          origin: 'all',
          uuid: this.filterUuid,
          filterEvents,
        },
      );
      this.destroyInputs$.pipe(take(1)).subscribe(() => {
        filterEvents.complete();
        componentRef.destroy();
      });
      componentRef.changeDetectorRef.detectChanges();
      return componentRef;
    });

    this.inputComponentsRef.forEach((_, index) => this.updateOptionInputs(index));

    combineLatest(this.inputComponentsRef.map(componentRef => componentRef.instance.state$))
      .pipe(takeUntil(this.destroyInputs$))
      .subscribe(filterState => {
        this.filterStateChanges.emit(filterState);
      });
  }

  private updateOptionInputs(index: number) {
    if (!this.inputComponentsRef[index]) return;

    const input = this.filterInputDefs[index];
    const inputService = this.inputServices[input.type];
    inputService.updateInputs(this.inputComponentsRef[index], {
      inputControl: this.filterControl.controls.value.controls[index],
      state: this.filterState[index],
    });
  }

  private disposeInputComponents() {
    this.destroyInputs$.next();
    this.inputsVCRef.clear();
    this.inputComponentsRef = [];
  }
}
